// Per-frame measurements: where each visible foot is on the board plane, and
// a coarse "how upright" body signal.

import { dist, mid, sub, type Vec2 } from './geometry';
import { toIso, toPlane, type PlaneMapping } from './mapping';
import { LM, lmVis, lmX, lmY, type PoseFrame } from './pose';

export type FootLabel = 'L' | 'R';

export interface FootObs {
  label: FootLabel;
  /** Plane positions, cm. `centre` is the heel–toe midpoint. */
  centre: Vec2;
  heel: Vec2;
  toe: Vec2;
  /** Heel→toe direction on the plane, radians. */
  angle: number;
  /** Heel–toe distance on the plane, cm. */
  length: number;
  /** Normalized image positions (for overlays). */
  heelImg: Vec2;
  toeImg: Vec2;
  /** Min visibility of heel and toe. */
  q: number;
}

export interface FrameObs {
  t: number;
  present: boolean;
  /** Feet that passed validity checks. */
  L: FootObs | null;
  R: FootObs | null;
  /** Feet-to-shoulders vertical extent, iso image units; null when unknown. */
  height: number | null;
  shoulderImg: Vec2 | null;
}

export interface FeatureOptions {
  minVisibility: number;
  /** Expected heel–toe length on the plane (cm); used for plausibility. */
  footLength?: number;
}

const FOOT_IDX = {
  L: { heel: LM.leftHeel, toe: LM.leftToe, ankle: LM.leftAnkle },
  R: { heel: LM.rightHeel, toe: LM.rightToe, ankle: LM.rightAnkle },
} as const;

const inFrame = (p: Vec2) => p.x > -0.02 && p.x < 1.02 && p.y > -0.02 && p.y < 1.02;

function footObs(lm: Float32Array, label: FootLabel, m: PlaneMapping, opt: FeatureOptions): FootObs | null {
  const idx = FOOT_IDX[label];
  const heelImg = { x: lmX(lm, idx.heel), y: lmY(lm, idx.heel) };
  const toeImg = { x: lmX(lm, idx.toe), y: lmY(lm, idx.toe) };
  const q = Math.min(lmVis(lm, idx.heel), lmVis(lm, idx.toe));
  if (q < opt.minVisibility || !inFrame(heelImg) || !inFrame(toeImg)) return null;
  const heel = toPlane(m, heelImg);
  const toe = toPlane(m, toeImg);
  if (![heel.x, heel.y, toe.x, toe.y].every(Number.isFinite)) return null;
  const d = sub(toe, heel);
  const length = Math.hypot(d.x, d.y);
  const ref = opt.footLength ?? 24;
  if (length < 0.35 * ref || length > 2 * ref) return null;
  const centre = mid(heel, toe);
  if (Math.abs(centre.x) > 400 || Math.abs(centre.y) > 600) return null;
  return { label, centre, heel, toe, angle: Math.atan2(d.y, d.x), length, heelImg, toeImg, q };
}

/** Feet closer than this are treated as the model collapsing both onto one foot. */
export const MERGED_FEET_CM = 8;

export function observeFrame(f: PoseFrame, m: PlaneMapping, opt: FeatureOptions): FrameObs {
  if (!f.lm) return { t: f.t, present: false, L: null, R: null, height: null, shoulderImg: null };
  const lm = f.lm;
  let L = footObs(lm, 'L', m, opt);
  let R = footObs(lm, 'R', m, opt);
  if (L && R && dist(L.centre, R.centre) < MERGED_FEET_CM) {
    L = null;
    R = null;
  }
  const iso = (i: number) => toIso({ x: lmX(lm, i), y: lmY(lm, i) }, m.aspect);
  const shoulder = mid(iso(LM.leftShoulder), iso(LM.rightShoulder));
  const shoulderVis = Math.min(lmVis(lm, LM.leftShoulder), lmVis(lm, LM.rightShoulder));
  // Lowest visible foot point stands in for floor level under the body.
  let floorY: number | null = null;
  for (const i of [LM.leftAnkle, LM.rightAnkle, LM.leftHeel, LM.rightHeel]) {
    if (lmVis(lm, i) >= 0.3) floorY = Math.max(floorY ?? -Infinity, iso(i).y);
  }
  const height = shoulderVis >= 0.3 && floorY !== null ? floorY - shoulder.y : null;
  return { t: f.t, present: true, L, R, height, shoulderImg: { x: shoulder.x / m.aspect, y: shoulder.y } };
}

// The live loop re-analyses a growing timeline after every frame; caching each
// frame's observation keeps that linear instead of quadratic.
const cache = new WeakMap<PoseFrame, { m: PlaneMapping; minVisibility: number; footLength?: number; obs: FrameObs }>();

export function observeAll(frames: readonly PoseFrame[], m: PlaneMapping, opt: FeatureOptions): FrameObs[] {
  return frames.map((f) => {
    const hit = cache.get(f);
    if (hit && hit.m === m && hit.minVisibility === opt.minVisibility && hit.footLength === opt.footLength) return hit.obs;
    const obs = observeFrame(f, m, opt);
    cache.set(f, { m, minVisibility: opt.minVisibility, footLength: opt.footLength, obs });
    return obs;
  });
}
